import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import SmartLink from "@/components/public/SmartLink";
import { SectionHead } from "@/components/landing/HeroSections";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Check, Quote, ArrowRight } from "lucide-react";

export function Pricing({ c }) {
  const p = c.pricing || {};
  const [pkgs, setPkgs] = useState([]);
  useEffect(() => {
    api.get("/packages").then((r) => setPkgs(r.data)).catch(() => {});
  }, []);
  if (!pkgs.length) return null;
  const popular = Math.min(parseInt(p.popular_index, 10) || 0, pkgs.length - 1);
  return (
    <section id="pricing" className="scroll-mt-24 bg-white" data-testid="pricing-section">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <SectionHead overline={p.overline} heading={p.heading} sub={p.subheading} center />
        <div className={`mx-auto mt-14 grid max-w-6xl items-stretch gap-6 ${pkgs.length >= 3 ? "lg:grid-cols-3" : "md:grid-cols-2"}`}>
          {pkgs.map((pkg, i) => <PriceCard key={pkg.id} pkg={pkg} p={p} featured={i === popular} i={i} />)}
        </div>
      </div>
    </section>
  );
}

function PriceCard({ pkg, p, featured, i }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
      className={`relative flex flex-col rounded-3xl p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 ${featured ? "hero-glow text-white shadow-2xl shadow-brand/20 ring-2 ring-brand" : "border border-slate-200 bg-white hover:shadow-xl"}`}
      data-testid={`pricing-card-${pkg.id}`}>
      {featured && p.popular_label && (
        <span className="absolute -top-3 left-8 rounded-full bg-brand-orange px-3 py-1 text-xs font-bold uppercase tracking-wider text-white" data-testid="pricing-popular-badge">{p.popular_label}</span>
      )}
      <h3 className={`font-heading text-xl font-bold ${featured ? "text-white" : "text-slate-900"}`}>{pkg.name}</h3>
      <div className="mt-5 flex items-baseline gap-1">
        <span className="font-heading text-5xl font-extrabold tracking-tight">{pkg.price > 0 ? `£${Number(pkg.price).toFixed(pkg.price % 1 ? 2 : 0)}` : (p.free_label || "Free")}</span>
        {pkg.price > 0 && <span className={featured ? "text-white/60" : "text-slate-400"}>/{(pkg.interval || "month").replace("month", "mo")}</span>}
      </div>
      <ul className="mt-7 flex-1 space-y-3">
        {(pkg.features || []).map((f) => (
          <li key={f} className={`flex items-start gap-2.5 text-[15px] ${featured ? "text-white/85" : "text-slate-600"}`}>
            <Check className={`mt-0.5 h-4 w-4 shrink-0 ${featured ? "text-brand-orange" : "text-brand"}`} strokeWidth={3} /> {f}
          </li>
        ))}
      </ul>
      <SmartLink href={p.cta_href || "/register"} data-testid={`pricing-cta-${pkg.id}`}
        className={`mt-8 inline-flex h-12 items-center justify-center rounded-full text-base font-bold transition-[background-color,transform] hover:-translate-y-0.5 ${featured ? "bg-brand text-white hover:bg-brand-hover" : "bg-slate-900 text-white hover:bg-slate-800"}`}>
        {p.cta_btn || "Get started"}
      </SmartLink>
    </motion.div>
  );
}

export function Testimonials({ c }) {
  const t = c.testimonials || {};
  const items = (t.items || []).filter((x) => x.quote);
  if (!items.length) return null;
  return (
    <section id="reviews" className="scroll-mt-24 bg-slate-50" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <SectionHead overline={t.overline} heading={t.heading} />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {items.map((x, i) => (
            <motion.figure key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="flex flex-col rounded-3xl border border-slate-200 bg-white p-8" data-testid={`testimonial-${i}`}>
              <Quote className="h-8 w-8 text-brand-orange" />
              <blockquote className="mt-5 flex-1 text-lg leading-relaxed text-slate-700">{x.quote}</blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                {x.avatar ? <img src={x.avatar} alt={x.name} className="h-11 w-11 rounded-full object-cover" /> :
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent font-bold text-brand">{(x.name || "?").replace(/[[\]]/g, "").charAt(0)}</span>}
                <div><p className="font-bold text-slate-900">{x.name}</p><p className="text-sm text-slate-500">{x.role}</p></div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Faq({ c }) {
  const f = c.faq || {};
  if (!(f.items || []).length) return null;
  return (
    <section id="faq" className="scroll-mt-24 bg-white" data-testid="faq-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 lg:grid-cols-12 lg:px-8 lg:py-28">
        <div className="lg:col-span-4"><SectionHead overline={f.overline} heading={f.heading} /></div>
        <Accordion type="single" collapsible className="space-y-3 lg:col-span-8">
          {f.items.map((it, i) => (
            <AccordionItem key={i} value={`q${i}`} className="rounded-2xl border border-slate-200 bg-white px-6 data-[state=open]:border-brand/40 data-[state=open]:shadow-lg data-[state=open]:shadow-brand/5">
              <AccordionTrigger className="py-5 text-left font-heading text-base font-bold text-slate-900 hover:no-underline md:text-lg" data-testid={`faq-q-${i}`}>{it.q}</AccordionTrigger>
              <AccordionContent className="pb-5 text-[15px] leading-relaxed text-slate-500">{it.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export function CtaBanner({ c }) {
  const t = c.cta || {};
  return (
    <section className="bg-white px-5 pb-20 lg:px-8 lg:pb-28" data-testid="cta-section">
      <div className="hero-glow relative mx-auto max-w-7xl overflow-hidden rounded-[36px] px-8 py-16 text-white lg:px-16 lg:py-20">
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand-orange/20 blur-3xl" />
        <div className="relative max-w-2xl">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">{t.title}</h2>
          <p className="mt-4 text-base text-white/70 md:text-lg">{t.subtitle}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <SmartLink href={t.primary_href || "/search"} className="group inline-flex h-12 items-center gap-2 rounded-full bg-brand px-7 font-bold text-white transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-brand-hover" data-testid="cta-find-btn">
              {t.primary_btn} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </SmartLink>
            <SmartLink href={t.secondary_href || "/register"} className="inline-flex h-12 items-center rounded-full border border-white/25 px-7 font-bold text-white transition-colors hover:bg-white/10" data-testid="cta-register-btn">
              {t.secondary_btn}
            </SmartLink>
          </div>
        </div>
      </div>
    </section>
  );
}
